import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {ElementMouseEvent} from 'util/Types';

interface CrumbLinkProps {
  className?: string;
  isLoading?: boolean;
  onClick?: (event: ElementMouseEvent) => void;
  // When provided, truncates the crumb with ellipsis at this width in pixels.
  // Should be paired with keepCrumbsInline=true on parent Breadcrumbs.
  maxCrumbWidth?: number;
  children?: React.ReactNode;
}

export function CrumbLink(props: CrumbLinkProps): JSX.Element {
  const className = props.className ?? 'genericBreadcrumbs-link';
  if (props.isLoading) {
    return <span className={className}>...</span>;
  } else {
    return (
      <a
        {...getTestProps({component: 'CrumbLink', element: 'root'})}
        className={classNames(className, props.maxCrumbWidth != null && css(styles.truncatedCrumb))}
        style={props.maxCrumbWidth != null ? {maxWidth: props.maxCrumbWidth} : undefined}
        onClick={props.onClick}
        // oxlint-disable-next-line jsx-a11y/no-redundant-roles -- href-less <a> needs role="link" for tabIndex; oxlint flags that as redundant on <a>
        role="link"
        tabIndex={0}
      >
        {props.children}
      </a>
    );
  }
}

interface BreadcrumbsProps {
  className: string;
  // When true, will use a flex layout to keep the crumbs on one line. Can be paired with maxCrumbWidth on children CrumbLinks to support truncation.
  // When false, will use a block layout to wrap the crumbs if needed and stack long crumbs on multiple lines.
  keepCrumbsInline?: boolean;
  children: React.ReactNode;
}

// TODO(T7355): Generalize component to support existing Breadcrumbs (i.e. nav) and change classNames to
// '.breadcrumbs'.
class Breadcrumbs extends React.Component<BreadcrumbsProps> {
  static defaultProps = {
    className: 'genericBreadcrumbs',
    keepCrumbsInline: false,
  };

  _renderCrumbs = (crumbs: Array<React.ReactNode>): React.ReactNode => {
    if (crumbs.length === 0) {
      return [];
    } else if (crumbs.length === 1) {
      return crumbs.concat(' / ');
    } else {
      // crumbs.length >= 2
      return [
        crumbs[0],
        ' / ',
        <span
          key="sub-crumbs"
          className={this.props.keepCrumbsInline ? css(styles.truncatedSubCrumb) : undefined}
        >
          {this._renderCrumbs(crumbs.slice(1))}
        </span>,
      ];
    }
  };

  override render(): JSX.Element {
    const children = React.Children.toArray(this.props.children);
    return (
      <div
        className={classNames(
          this.props.className,
          'legacy-font-proxima-13',
          this.props.keepCrumbsInline && css(styles.breadcrumbsWithTruncation)
        )}
      >
        {this._renderCrumbs(children)}
      </div>
    );
  }
}

export default Breadcrumbs;

const styles = StyleSheet.create({
  breadcrumbsWithTruncation: {display: 'flex'},
  truncatedSubCrumb: {
    display: 'flex',
  },
  truncatedCrumb: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});
